import React from 'react';
import NavBar from '../components/layout/NavBar';
import SiteFooter from '../components/layout/SiteFooter';
import SEO from '../components/SEO';
import Breadcrumbs from '../components/Breadcrumbs';
import FAQSection from '../components/FAQSection';
import { useWaitlistModal } from '../context/WaitlistModalContext';
import { webPageSchema, breadcrumbSchema, faqSchema } from '../schema';

const faqItems = [
  { question: 'When is SaniQ launching?', answer: 'SaniQ is targeting a 2027 launch in Australia. Waitlist members will be notified first as the launch date is confirmed.' },
  { question: 'How much will SaniQ cost?', answer: 'Pricing has not been finalized. Founding-member waitlist signups will receive exclusive early-adopter pricing ahead of general availability.' },
  { question: 'Will there be a limited first production run?', answer: 'Yes, early units are expected to be limited. Waitlist members get first access when initial production units become available.' },
  { question: 'What do I get for joining the waitlist?', answer: 'Launch-day notifications, founding-member pricing, priority access to initial production units, and product progress updates before public launch.' },
  { question: 'Will SaniQ be available outside Australia?', answer: 'The initial 2027 launch is focused on Australia. Broader availability will be communicated to waitlist members as plans develop.' },
  { question: 'Is there a cost to join the waitlist?', answer: 'No, joining the waitlist is free and only requires an email address.' },
];

const Waitlist = () => {
  const { openWaitlistModal } = useWaitlistModal();

  return (
    <div className="relative w-full min-h-screen">
      <SEO
        title="Join the Waitlist: Launching 2027"
        description="Register for SaniQ launch updates. Be first to know when the world's first smart disinfectant doormat becomes available in Australia, and get founding-member pricing."
        path="/waitlist"
        schema={[
          webPageSchema({ title: 'Join the SaniQ Waitlist', description: 'Register for SaniQ launch updates.', path: '/waitlist' }),
          breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Updates', path: '/waitlist' }]),
          faqSchema(faqItems),
        ]}
      />
      <NavBar />
      <Breadcrumbs trail={[{ name: 'Home', path: '/' }, { name: 'Updates', path: '/waitlist' }]} />

      <section className="py-12 px-8 relative overflow-hidden">
        <div className="absolute inset-0 bg-cyan-500/5 blur-[150px] rounded-full translate-y-1/2" />
        <div className="max-w-4xl mx-auto glass rounded-3xl p-12 md:p-20 text-center relative z-10">
          <h1 className="text-4xl md:text-5xl font-light mb-6 tracking-tight text-white">Launching 2027.</h1>
          <p className="text-white/45 text-sm md:text-base leading-relaxed max-w-xl mx-auto mb-10">
            Be first. Be protected. SaniQ is coming to Australia in 2027, so join the waitlist for launch updates and founding-member benefits.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl mx-auto mb-12 text-left">
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] px-5 py-4">
              <p className="text-cyan-400 text-[10px] uppercase tracking-widest mb-1">Launch-Day Notifications</p>
              <p className="text-white/50 text-xs leading-relaxed">Be first to know when SaniQ becomes available.</p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] px-5 py-4">
              <p className="text-cyan-400 text-[10px] uppercase tracking-widest mb-1">Founding-Member Pricing</p>
              <p className="text-white/50 text-xs leading-relaxed">Get exclusive early-adopter pricing.</p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] px-5 py-4">
              <p className="text-cyan-400 text-[10px] uppercase tracking-widest mb-1">First in Line</p>
              <p className="text-white/50 text-xs leading-relaxed">Reserve your place for initial production units.</p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] px-5 py-4">
              <p className="text-cyan-400 text-[10px] uppercase tracking-widest mb-1">Exclusive Updates</p>
              <p className="text-white/50 text-xs leading-relaxed">Receive product progress updates before launch.</p>
            </div>
          </div>

          <button
            type="button"
            onClick={openWaitlistModal}
            className="w-full md:w-auto px-10 py-4 bg-cyan-500 text-black font-bold rounded-full text-sm uppercase tracking-widest hover:scale-105 transition-transform"
          >
            Register
          </button>
        </div>
      </section>

      <FAQSection heading="Waitlist Frequently Asked Questions" items={faqItems} />

      <SiteFooter />
    </div>
  );
};

export default Waitlist;
